const wnCartDrawerCopy = window.WN_I18N?.cart || {};
const wnCartRoot = window.Shopify?.routes?.root || '/';

class WnCartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.sectionId = this.dataset.sectionId;
    this.sectionUrl = this.dataset.sectionUrl || `${wnCartRoot}cart`;
    this.dialog = this.querySelector('[data-cart-drawer-dialog]');
    this.content = this.querySelector('[data-cart-drawer-content]');
    this.overlay = this.querySelector('[data-cart-drawer-overlay]');
    this.closeButton = this.querySelector('[data-cart-drawer-close]');
    this.status = this.querySelector('[data-cart-status]');
    this.isOpen = false;
    this.refreshing = false;
    this.refreshAgain = false;
    this.openAfterRefresh = false;
    this.closeRevision = 0;
    this.content?.removeAttribute('inert');
    document.addEventListener('click', (event) => this.onDocumentClick(event), { signal });
    document.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    document.addEventListener('wn:cart:updated', (event) => this.onCartUpdated(event), { signal });
    document.addEventListener('wn:cart:error', (event) => this.showError(event.detail?.message), { signal });
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('change', (event) => this.onChange(event), { signal });
    this.overlay?.addEventListener('click', () => this.close(), { signal });
    this.closeButton?.addEventListener('click', () => this.close(), { signal });
  }

  disconnectedCallback() { this.abortController?.abort(); this.ready = false; }

  clampQuantity(input, value) {
    const min = Number(input.dataset.cartMin ?? input.min ?? 0);
    const maxRaw = Number(input.dataset.cartMax ?? input.max);
    const max = Number.isFinite(maxRaw) && maxRaw > 0 ? maxRaw : Infinity;
    return Math.min(max, Math.max(Number.isFinite(min) ? min : 0, Number(value) || 0));
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-cart-drawer-open]');
    if (!trigger || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.lastFocused = trigger;
    this.open(trigger);
    if (window.WNCart?.refresh) {
      window.setTimeout(() => {
        window.WNCart.refresh({ source: 'cart-drawer-open', openDrawer: false }).catch(() => null);
      }, 0);
    }
  }

  onKeydown(event) {
    if (!this.isOpen) return;
    if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
    if (event.key !== 'Tab') return;
    const focusable = this.focusableElements();
    if (!focusable.length) { event.preventDefault(); this.closeButton?.focus({ preventScroll: true }); return; }
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (!this.dialog?.contains(document.activeElement)) { event.preventDefault(); (event.shiftKey ? last : first).focus(); return; }
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  onCartUpdated(event) { this.openAfterRefresh = this.openAfterRefresh || event.detail?.openDrawer === true; this.scheduleRefresh(); }

  async onClick(event) {
    const navigationLink = event.target.closest('a[href]');
    if (navigationLink && !navigationLink.matches('[data-cart-remove]')) {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); window.location.assign(navigationLink.href); return;
    }
    const upsellButton = event.target.closest('[data-cart-upsell-add]');
    if (upsellButton) {
      event.preventDefault();
      if (this.isMutationLocked() || !window.WNCart?.add) return;
      const variantId = Number(upsellButton.dataset.variantId || upsellButton.value || 0);
      if (!variantId) return;
      this.setBusy(true, wnCartDrawerCopy.addingFilters || 'Adding replacement filters…');
      try { await window.WNCart.add({ items: [{ id: variantId, quantity: 1 }] }, { source: 'cart-drawer-upsell', openDrawer: false }); }
      catch (error) { this.showError(error.message); this.setBusy(false); }
      return;
    }
    const quantityButton = event.target.closest('[data-cart-quantity-change]');
    if (quantityButton) {
      event.preventDefault();
      if (this.isMutationLocked() || !window.WNCart?.change) return;
      const item = quantityButton.closest('[data-cart-line]');
      const input = item?.querySelector('[data-cart-quantity-input]');
      if (!item || !input) return;
      const quantity = this.clampQuantity(input, (parseInt(input.value, 10) || 0) + Number(quantityButton.dataset.cartQuantityChange || 0));
      input.value = quantity;
      await this.changeLine(item, quantity);
      return;
    }
    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      if (this.isMutationLocked() || !window.WNCart?.change) return;
      const item = remove.closest('[data-cart-line]');
      if (item) await this.changeLine(item, 0);
    }
  }

  async onChange(event) {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input || this.isMutationLocked() || !window.WNCart?.change) return;
    const item = input.closest('[data-cart-line]');
    if (!item) return;
    const quantity = this.clampQuantity(input, parseInt(input.value, 10) || 0);
    input.value = quantity;
    await this.changeLine(item, quantity);
  }

  isMutationLocked() { return this.refreshing || this.hasAttribute('aria-busy'); }

  async changeLine(item, quantity) {
    this.setBusy(true, quantity === 0 ? (wnCartDrawerCopy.removingItem || 'Removing item…') : (wnCartDrawerCopy.updatingQuantity || 'Updating quantity…'));
    try {
      const key = item.dataset.cartKey;
      await window.WNCart.change(key ? { id: key, quantity } : { line: Number(item.dataset.cartLine), quantity }, { source: 'cart-drawer', openDrawer: false });
    } catch (error) { this.showError(error.message); this.setBusy(false); }
  }

  open(trigger = null) {
    if (!this.dialog) return;
    if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
    this.lastFocused = trigger || this.lastFocused || document.activeElement;
    this.isOpen = true; this.hidden = false; this.setAttribute('aria-hidden', 'false');
    document.documentElement.setAttribute('data-cart-drawer-open', '');
    requestAnimationFrame(() => this.closeButton?.focus({ preventScroll: true }));
  }

  close() {
    if (!this.isOpen) return;
    this.closeRevision += 1; this.openAfterRefresh = false; this.isOpen = false;
    this.setAttribute('aria-hidden', 'true'); document.documentElement.removeAttribute('data-cart-drawer-open');
    const focusTarget = this.lastFocused;
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this.hidden = true; this.closeTimer = null;
      if (focusTarget instanceof HTMLElement && document.contains(focusTarget)) focusTarget.focus({ preventScroll: true });
    }, 220);
  }

  focusableElements() {
    if (!this.dialog) return [];
    return [...this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((element) => !element.hidden && element.offsetParent !== null);
  }

  setBusy(state, message = '') {
    this.toggleAttribute('aria-busy', state); this.content?.removeAttribute('inert');
    if (state && this.status) this.status.classList.remove('is-error');
    if (this.status && message) this.status.textContent = message;
  }
  showError(message) { this.status = this.querySelector('[data-cart-status]'); if (!this.status || !message) return; this.status.textContent = message; this.status.classList.add('is-error'); }
  scheduleRefresh() { if (this.refreshing) { this.refreshAgain = true; return; } this.refreshSection(); }
  sectionRequestUrl() {
    const url = new URL(this.sectionUrl, location.origin), currentUrl = new URL(location.href);
    ['preview_theme_id', '_fd', 'pb'].forEach((parameter) => { const value = currentUrl.searchParams.get(parameter); if (value) url.searchParams.set(parameter, value); });
    url.searchParams.set('section_id', this.sectionId); url.searchParams.set('_wn_cart', String(Date.now())); return url;
  }
  async fetchSectionReplacement() {
    const response = await fetch(this.sectionRequestUrl(), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(wnCartDrawerCopy.viewRefreshError || 'The cart view could not be refreshed.');
    const replacement = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-cart-drawer-content]');
    if (!replacement) throw new Error(wnCartDrawerCopy.viewMissing || 'The refreshed cart view is missing.');
    return replacement;
  }
  async refreshSection() {
    if (!this.sectionId || !this.content) return;
    this.refreshing = true;
    const keepOpen = this.isOpen || this.openAfterRefresh, closeRevision = this.closeRevision;
    this.setBusy(true, wnCartDrawerCopy.updating || 'Updating cart…');
    try {
      const replacement = await this.fetchSectionReplacement();
      this.content.innerHTML = replacement.innerHTML;
      this.content.removeAttribute('inert'); this.status = this.querySelector('[data-cart-status]');
      this.setBusy(false); this.refreshing = false; this.openAfterRefresh = false;
      document.dispatchEvent(new CustomEvent('wn:cart:view-refreshed', { detail: { root: this, context: 'drawer' } }));
      if (keepOpen && !this.isOpen && this.closeRevision === closeRevision) this.open(this.lastFocused || document.activeElement);
      if (this.refreshAgain) { this.refreshAgain = false; this.refreshSection(); }
    } catch (error) { this.refreshing = false; this.setBusy(false); this.showError(error.message); }
  }
}
if (!customElements.get('wn-cart-drawer')) customElements.define('wn-cart-drawer', WnCartDrawer);
